import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchUsageSummary } from "@/lib/api/admin";
import type { UsageSummary } from "@/lib/api/admin";

export function UsagePage() {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useQuery<UsageSummary, Error>({
    queryKey: ["admin", "usage"],
    queryFn: () => fetchUsageSummary(14),
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <p className="text-sm text-muted">{t("admin.usage.loading")}</p>
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="rounded-card border border-error/20 bg-error/5 p-4 text-sm text-error">
          {t("admin.usage.error", { message: error?.message ?? "" })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      {/* Page header */}
      <div className="border-b border-pantera-100 bg-white px-10 pb-0 pt-7">
        <div className="flex items-start justify-between">
          <div>
            <p className="eyebrow">Panel administrativo</p>
            <h1 className="mt-1.5 text-3xl font-black tracking-tight text-pantera-900">
              {t("admin.usage.title")}
            </h1>
            <p className="mt-1 text-[13px] text-muted">
              {t("admin.usage.window", { days: data.daily.length })}
            </p>
          </div>
          <div className="flex gap-2 pt-1">
            <button className="btn-ghost text-sm">Filtros</button>
            <button className="btn-accent rounded-pill px-4 py-2 text-sm">Exportar reporte</button>
          </div>
        </div>
        {/* Tab strip */}
        <div className="mt-5 flex gap-7">
          {["Vista general", "Consultas", "Usuarios", "Costos"].map((tab, i) => (
            <div key={tab} className="relative pb-3">
              <span
                className={`text-[13px] ${i === 0 ? "font-bold text-pantera-900" : "font-medium text-muted"}`}
              >
                {tab}
              </span>
              {i === 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-[3px] rounded-full bg-accent-500" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-6 px-10 py-8">
        <Totals totals={data.totals} />

        <div className="grid gap-5 md:grid-cols-2">
          <ChartCard
            title={t("admin.usage.dailyResponses")}
            sub="Mensajes por día · últimas 2 semanas"
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 4" stroke="#ede8ff" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#6b7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                <Tooltip
                  contentStyle={{ borderRadius: 12, border: "1px solid #ede8ff", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line
                  type="monotone"
                  dataKey="responses"
                  name={t("admin.usage.responsesLabel")}
                  stroke="#3b037d"
                  strokeWidth={2.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard title={t("admin.usage.dailyCost")} sub="Costo USD · por día">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={data.daily}>
                <CartesianGrid strokeDasharray="3 4" stroke="#ede8ff" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#6b7280" }} />
                <YAxis tick={{ fontSize: 11, fill: "#6b7280" }} />
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(4)}`}
                  contentStyle={{ borderRadius: 12, border: "1px solid #ede8ff", fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar
                  dataKey="cost_usd"
                  name={t("admin.usage.costLabel")}
                  fill="#3b037d"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <TableCard title={t("admin.usage.byUser")}>
            {data.by_user.length === 0 ? (
              <p className="text-sm text-muted">{t("admin.usage.empty")}</p>
            ) : (
              <table className="w-full table-auto text-left text-xs">
                <thead>
                  <tr className="bg-[#f5f0ff]">
                    <th className="rounded-l-lg px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.user")}
                    </th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.responses")}
                    </th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.tokens")}
                    </th>
                    <th className="rounded-r-lg px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.cost")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_user.map((row) => (
                    <tr key={row.user_id} className="border-t border-pantera-100">
                      <td className="px-3 py-2">
                        <div className="font-semibold text-pantera-900">{row.display_name}</div>
                        <div className="text-[10px] text-muted">{row.email}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{row.responses}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {(row.input_tokens + row.output_tokens).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">${row.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>

          <TableCard title={t("admin.usage.byModel")}>
            {data.by_model.length === 0 ? (
              <p className="text-sm text-muted">{t("admin.usage.empty")}</p>
            ) : (
              <table className="w-full table-auto text-left text-xs">
                <thead>
                  <tr className="bg-[#f5f0ff]">
                    <th className="rounded-l-lg px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.provider")}
                    </th>
                    <th className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.model")}
                    </th>
                    <th className="px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.calls")}
                    </th>
                    <th className="rounded-r-lg px-3 py-2.5 text-right text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-700">
                      {t("admin.usage.col.cost")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_model.map((row) => (
                    <tr
                      key={`${row.provider}-${row.model}`}
                      className="border-t border-pantera-100"
                    >
                      <td className="px-3 py-2 font-mono">{row.provider}</td>
                      <td className="px-3 py-2 font-mono">{row.model}</td>
                      <td className="px-3 py-2 text-right">{row.calls}</td>
                      <td className="px-3 py-2 text-right">${row.cost_usd.toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </TableCard>
        </div>
      </div>
    </div>
  );
}

function Totals({ totals }: { totals: UsageSummary["totals"] }) {
  const { t } = useTranslation();
  const cards = [
    { label: t("admin.usage.totals.responses"), value: totals.responses.toLocaleString() },
    { label: t("admin.usage.totals.users"), value: totals.users.toLocaleString() },
    {
      label: t("admin.usage.totals.tokens"),
      value: (totals.total_input_tokens + totals.total_output_tokens).toLocaleString(),
    },
    { label: t("admin.usage.totals.cost"), value: `$${totals.total_cost_usd.toFixed(4)}` },
    { label: t("admin.usage.totals.avgLatency"), value: `${totals.avg_latency_ms} ms` },
    { label: t("admin.usage.totals.p95Latency"), value: `${totals.p95_latency_ms} ms` },
  ];

  return (
    <dl className="grid grid-cols-2 gap-4 md:grid-cols-6">
      {cards.map((c) => (
        <div key={c.label} className="card px-4 py-4">
          <dt className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted">
            {c.label}
          </dt>
          <dd className="mt-2 text-[32px] font-black leading-none tracking-tight text-pantera-700">
            {c.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ChartCard({
  title,
  sub,
  children,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card p-5">
      <div className="mb-4">
        <h3 className="text-[14px] font-bold text-pantera-900">{title}</h3>
        {sub && <p className="mt-0.5 text-[11px] text-muted">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

function TableCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card p-5">
      <h3 className="mb-4 text-[14px] font-bold text-pantera-900">{title}</h3>
      {children}
    </div>
  );
}
